import React, { FC } from 'react'
import classNames from 'classnames'
import { FormattedDate } from 'react-intl'

import { IPlayerProfileResponse } from 'protos'

import { Grid, Typography, makeStyles, Theme, Box, List, ListItem, ListItemText } from '@material-ui/core'

import Paper from 'components/Paper'
import FormattedMessage from 'components/FormattedMessage'

type Props = {
  profile: IPlayerProfileResponse
}

const useStyles = makeStyles<Theme>((theme) => ({
  faded: {
    opacity: .7,
  },
  fontSize10: {
    fontSize: 10,
  },
  fontSize12: {
    fontSize: 12,
  },
  fontSize14: {
    fontSize: 14,
  },
  bolder: {
    fontWeight: 700,
  },
  spacer: {
    width: 40,
  },
  separator: {
    position: 'relative',
    '&::before': {
      width: 1,
      left: '50%',
      right: '50%',
      content: '""',
      position: 'absolute',
      top: theme.spacing(1.5),
      bottom: theme.spacing(1.5),
      background: `
        linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, .2) 50%,
          rgba(255, 255, 255, 0) 100%
        )
      `,
    },
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  grayishBlue: {
    color: '#8EA5C2',
  },
  transparentBg: {
    backgroundColor: 'transparent',
  },
  divider: {
    borderBottomColor: 'rgba(142, 165, 194, 0.22)',
  },
  gutters: {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
  },
}))

const HistoryTab: FC<Props> = ({ profile: { player, previousMatch } }) => {
  const classes = useStyles()

  return (
    <Paper
      color="accent"
      className={classes.transparentBg}
    >
      <Box
        py={2}
        px={2.5}
        className={classes.separator}
      >
        <Grid
          container
          wrap="nowrap"
          alignItems="center"
        >
          <Grid xs item>
            <Typography
              className={
                classNames(
                  classes.faded,
                  classes.uppercase,
                  classes.fontSize10,
                  classes.grayishBlue,
                )
              }
            >
              <FormattedMessage id="profile_averagescore" />
            </Typography>
          </Grid>

          <Grid xs item>
            <Box px={0.5}>
              <Typography
                variant="h5"
                align="right"
                color="inherit"
                className={classes.bolder}
              >
                {Math.round(player?.averageScore ?? 0)}
              </Typography>
            </Box>
          </Grid>

          <Grid item>
            <Typography
              className={
                classNames(
                  classes.faded,
                  classes.fontSize12,
                  classes.grayishBlue,
                )
              }
            >
              <FormattedMessage id="common_unit_point" />
            </Typography>
          </Grid>

          <Grid
            item
            className={classes.spacer}
          />

          <Grid xs item>
            <Typography
              className={
                classNames(
                  classes.faded,
                  classes.uppercase,
                  classes.fontSize10,
                  classes.grayishBlue,
                )
              }
            >
              <FormattedMessage id="profile_averageplaytime" />
            </Typography>
          </Grid>

          <Grid xs item>
            <Box px={0.5}>
              <Typography
                variant="h5"
                align="right"
                color="inherit"
                className={classes.bolder}
              >
                {Math.round(player?.averageMinutesPlayed ?? 0)}
              </Typography>
            </Box>
          </Grid>

          <Grid item>
            <Typography
              className={
                classNames(
                  classes.faded,
                  classes.fontSize12,
                  classes.grayishBlue,
                )
              }
            >
              <FormattedMessage id="common_unit_minutes" />
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <List>
        <ListItem
          dense
          divider
          classes={{ divider: classes.divider, gutters: classes.gutters }}
        >
          <ListItemText
            primary={<FormattedMessage id="playerprofile_history_previousmatch" />}
            primaryTypographyProps={{
              color: 'inherit',
              variant: 'body1',
              className: classes.fontSize12,
            }}
          />
        </ListItem>

        {previousMatch?.map(match => (
          <ListItem
            dense
            divider
            key={match.match?.matchId!}
            classes={{ divider: classes.divider, gutters: classes.gutters }}
          >
            <Grid
              container
              justify="space-between"
            >
              <Grid item>
                <ListItemText
                  primary={
                    <FormattedMessage
                      id="profile_history_opponent"
                      values={{
                        name: match.match?.homeTeam?.teamId === match.team?.teamId
                          ? match.match?.awayTeam?.name
                          : match.match?.homeTeam?.name
                        ?? '???'
                      }}
                    />
                  }
                  secondary={
                    <FormattedDate
                      value={Number((match.match?.kickoff ?? 0)) * 1000}
                    />
                  }
                  primaryTypographyProps={{
                    color: 'inherit',
                    variant: 'body1',
                    className: classes.fontSize14,
                  }}
                  secondaryTypographyProps={{
                    color: 'inherit',
                    variant: 'body1',
                    className: classNames(classes.grayishBlue, classes.fontSize12, classes.faded),
                  }}
                />
              </Grid>
              <Grid item>
                <ListItemText
                  primary={
                    <>
                      {match.score ?? 0}
                      {' '}
                      <FormattedMessage id="common_unit_point" />
                    </>
                  }
                  secondary={
                    <FormattedMessage
                      id="profile_history_played_minutes"
                      values={{ minutes: match.minutesPlayed ?? 0 }}
                    />
                  }
                  primaryTypographyProps={{
                    align: 'right',
                    color: 'inherit',
                    variant: 'body1',
                    className: classes.fontSize14,
                  }}
                  secondaryTypographyProps={{
                    align: 'right',
                    color: 'inherit',
                    variant: 'body1',
                    className: classNames(classes.grayishBlue, classes.fontSize12, classes.faded),
                  }}
                />
              </Grid>
            </Grid>
          </ListItem>
        ))}
      </List>
    </Paper>
  )
}

export default HistoryTab
try {
    // @ts-ignore
    HistoryTab.displayName = "HistoryTab";
    // @ts-ignore
    HistoryTab.__docgenInfo = { "description": "", "displayName": "HistoryTab", "props": { "profile": { "defaultValue": null, "description": "", "name": "profile", "required": true, "type": { "name": "any" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/PlayerLayer/components/PlayerProfile/tabs/HistoryTab/HistoryTab.tsx#HistoryTab"] = { docgenInfo: HistoryTab.__docgenInfo, name: "HistoryTab", path: "src/components/PlayerLayer/components/PlayerProfile/tabs/HistoryTab/HistoryTab.tsx#HistoryTab" };
}
catch (__react_docgen_typescript_loader_error) { }